import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import { styled } from '@mui/material/styles';
import { Typography } from '@components/Typography';
import { useTranslation } from '@hooks';
import { formatPercentNumberParts } from '@helpers/formatNumbers';
import { MetricGrid } from '@pages/Analytics/Presence/PresenceRates';
import MetricItem from '@features/Analytics/Common/MetricItem';

const VisibilityMetricGrid = styled(MetricGrid)({
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gridTemplateAreas: '"metric1 metric2 metric3"',
    '@media (max-width: 1120px)': {
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gridTemplateAreas: '"metric1 metric2" "metric3 metric3"',
    },
    '@media (max-width: 600px)': {
        gridTemplateColumns: '100%',
        gridTemplateAreas: '"metric1" "metric2" "metric3"',
    },
});

const METRIC_AREAS = ['metric1', 'metric2', 'metric3'];

const NO_DATA = { prefix: '', number: '—', suffix: '' };

const VisibilityRates = ({ summary, error, isLoading }) => {
    const { t } = useTranslation();
    // The backend always compares against the nearest scan ≥ 90 days old, independent of
    // the selected timeframe — label it for what it actually measures.
    const deltaLabel = t('AnalyticsVisibility.vs90DaysAgo');

    // Skeleton while refetching so a filter change doesn't leave the previous filter's
    // metrics on screen looking like the current selection's.
    if (isLoading) {
        return (
            <VisibilityMetricGrid>
                {METRIC_AREAS.map(area => (
                    <Skeleton
                        key={area}
                        variant="rectangular"
                        height={140}
                        sx={{ gridArea: area, borderRadius: '8px' }}
                    />
                ))}
            </VisibilityMetricGrid>
        );
    }

    // The summary request can fail independently of the table — surface it instead of
    // rendering all-em-dash cards as though the data simply doesn't exist yet.
    if (error) {
        return (
            <Paper elevation={1} sx={{ padding: '24px', borderRadius: '8px' }}>
                <Typography variant="body1" align="center" sx={{ display: 'block' }}>
                    {t('AnalyticsVisibility.loadError')}
                </Typography>
            </Paper>
        );
    }

    const avgVisibility = summary?.avgVisibility;
    const avgVisibilityDelta = summary?.avgVisibilityDelta;
    const locationsTop3 = summary?.locationsTop3;
    const locationsTotal = summary?.locationsTotal;
    const avgRank = summary?.avgRank;
    const avgRankDelta = summary?.avgRankDelta;

    return (
        <VisibilityMetricGrid>
            <MetricItem
                title={t('AnalyticsVisibility.averageVisibility')}
                value={avgVisibility != null ? formatPercentNumberParts(avgVisibility * 100) : NO_DATA}
                data={{ delta: avgVisibilityDelta != null ? avgVisibilityDelta * 100 : null }}
                deltaLabel={deltaLabel}
                withPercentageDelta
                styles={{ gridArea: 'metric1' }}
            />
            <MetricItem
                title={t('AnalyticsVisibility.locationsInTop3')}
                value={
                    locationsTop3 != null && locationsTotal != null
                        ? { prefix: '', number: String(locationsTop3), suffix: `/ ${locationsTotal}` }
                        : NO_DATA
                }
                data={{ delta: null }}
                deltaLabel={deltaLabel}
                styles={{ gridArea: 'metric2' }}
            />
            <MetricItem
                title={t('AnalyticsVisibility.averageMapRank')}
                value={avgRank != null ? { prefix: '', number: avgRank.toFixed(1), suffix: '' } : NO_DATA}
                data={{ delta: avgRankDelta }}
                deltaLabel={deltaLabel}
                formatDelta={value => value.toFixed(1)}
                withInvertion
                styles={{ gridArea: 'metric3' }}
            />
        </VisibilityMetricGrid>
    );
};

export default VisibilityRates;
